import * as React from 'react';
import { format, isAfter, isBefore } from 'date-fns';
import { CalendarIcon, Clock2Icon } from 'lucide-react';
import { cn } from 'lib/utils';

import { FormControl, FormItem, FormMessage } from '../form';
import { Label } from '../label';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';
import { Button } from '../button';
import { Calendar } from '../calendar';
import { Card, CardContent, CardFooter } from '../card';
import { Input } from '../input';

interface DatePickerGsProps {
  label?: string;
  fieldValue?: Date;
  fieldOnChange: (...event: any[]) => void;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  /** pass false to ensure the options popover renders inside a dialog */
  portal?: boolean;
}

const getDateTime = ({ date, time }: { date?: Date, time?: string }): Date | undefined => {
  if (!date) return undefined;
  if (!time) return date;

  const [hours, minutes, seconds] = time.split(':').map(Number);
  date.setHours(hours || 0, minutes || 0, seconds || 0);
  return date;
};

const DateTimePickerGs
  = React.forwardRef<HTMLBaseElement, DatePickerGsProps>(({
                                                            label,
                                                            minDate,
                                                            maxDate,
                                                            fieldValue,
                                                            fieldOnChange,
                                                            disabled,
                                                            portal,
                                                          }, ref) => {
    const handleDateChange = React.useCallback((date?: Date) => {
      const dateTime = getDateTime({
        date,
        time: fieldValue && format(fieldValue, 'HH:mm:ss'),
      });
      fieldOnChange(dateTime);
    }, [fieldValue, fieldOnChange]);

    const handleTimeChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
      const dateTime = getDateTime({
        date: fieldValue,
        time: event.target.value,
      });
      fieldOnChange(dateTime);
    }, [fieldValue, fieldOnChange]);

    const checkIfDataIsValid = React.useCallback((date: Date) => {
      const zeroHourDate = new Date(format(date, 'yyyy-MM-dd'));

      const validAgainstMinDate = (() => {
        if (!minDate) return true;

        const zeroHourMinDate = new Date(format(minDate, 'yyyy-MM-dd'));
        return !isBefore(zeroHourDate, zeroHourMinDate);
      })();

      const validAgainstMaxDate = (() => {
        if (!maxDate) return true;

        const zeroHourMaxDate = new Date(format(maxDate, 'yyyy-MM-dd'));
        return !isAfter(zeroHourDate, zeroHourMaxDate);
      });

      return validAgainstMinDate && validAgainstMaxDate;

    }, [minDate, maxDate]);

    return (
      <FormItem>
        <div className="grid w-full items-center gap-1.5">
          {label && (
            <Label className="font-GilroySemibold text-sm text-slate-800">
              {label}
            </Label>
          )}
          <Popover>
            <PopoverTrigger asChild>
              <FormControl>
                <Button
                  variant={'outline'}
                  disabled={disabled}
                  className={cn(
                    'font-sans pl-3 text-left border-solid font-normal focus-visible:ring-primary-500',
                    !fieldValue && 'text-muted-foreground',
                  )}
                >
                  {fieldValue ? (
                    format(fieldValue, 'PPP p')
                  ) : (
                    <span>Selecione ...</span>
                  )}
                  <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                </Button>
              </FormControl>
            </PopoverTrigger>
            <PopoverContent portal={portal} className="w-auto p-0" align="start">
              <Card className="w-fit py-4">
                <CardContent className="px-4">
                  <Calendar
                    mode="single"
                    selected={fieldValue}
                    onSelect={handleDateChange}
                    className="bg-transparent p-0"
                    fromDate={minDate}
                    toDate={maxDate}
                    disabled={(date) => {
                      return !checkIfDataIsValid(date);
                    }}
                  />
                </CardContent>
                <CardFooter className="flex flex-col gap-2 border-t px-4 !pt-4">
                  <div className="flex w-full pt-5 flex-col gap-3">
                    <Label htmlFor="time-from">Hora:</Label>
                    <div className="relative flex w-full items-center">
                      <Clock2Icon
                        className="text-muted-foreground pointer-events-none absolute left-2.5 size-4 select-none" />
                      <Input
                        id="time-from"
                        type="time"
                        step="1"
                        value={format(fieldValue || new Date(), 'HH:mm:ss')}
                        onChange={handleTimeChange}
                        className="appearance-none pl-8 [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
                        disabled={!fieldValue}
                      />
                    </div>
                  </div>
                </CardFooter>
              </Card>
            </PopoverContent>
          </Popover>
          <FormMessage />
        </div>
      </FormItem>
    );
  },
);

DateTimePickerGs.displayName = 'DateTimePickerGs';
export default DateTimePickerGs;


